import { Node } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'

import { Embed as EmbedType } from '@tribeplatform/gql-client/types'

import { Embed as EmbedComponent } from './Embed.js'

export interface EmbedOptions {
  embeds: EmbedType[]
  HTMLAttributes: Record<string, unknown>
  getEmbed: unknown
}

interface EmbedAttr {
  embed?: EmbedType
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    embed: {
      /**
       * Add an embed
       */
      setEmbed: (options: EmbedAttr) => ReturnType
      removePreview: (embed: EmbedType) => ReturnType
    }
  }
}

export const Embed = Node.create<EmbedOptions>({
  name: 'embed',

  inline: false,
  group: 'block',

  draggable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      embeds: [],
      getEmbed: undefined,
    }
  },

  addAttributes() {
    return {
      embed: {
        default: undefined,
        rendered: false,
      },
      url: {
        default: undefined,
        rendered: false,
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type=embed]',
        getAttrs: dom => {
          if (typeof dom === 'string') {
            return {}
          }
          const element = dom as HTMLImageElement
          const id = element.getAttribute('data-id')
          const url = element.getAttribute('data-embed-url')
          const embed = this.options.embeds?.find(e => e.id === id)

          if (!embed && !url) {
            return {}
          }

          return {
            url,
            embed,
          }
        },
      },
    ]
  },

  renderHTML({ node }) {
    const { embed, url } = node.attrs
    if (!embed && !url) {
      return ['div']
    }
    return [
      'div',
      {
        'data-type': 'embed',
        'data-id': embed?.id,
        'data-embed-url': url || embed?.url,
      },
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(EmbedComponent)
  },

  addCommands() {
    return {
      setEmbed:
        attrs =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs,
          })
        },
    }
  },
})
